import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Mail, Bug } from "lucide-react";

export function AdminBugs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/bug-reports"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    await api.put(`/admin/bug-reports/${id}`, patch);
    load();
  };

  return (
    <div data-testid="admin-bugs">
      <div className="dms-label mb-3">User reports</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Bug reports</h1>
      <div className="mt-6 space-y-3">
        {loading ? <div className="text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div> :
         items.map((b) => (
          <div key={b.id} className={`dms-card p-4 sm:p-5 ${b.is_read ? "opacity-70" : ""}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="font-heading font-bold text-[#0A2342] flex items-center gap-2"><Bug className="w-4 h-4 text-[#B91C1C]" /> {b.title}</div>
                <div className="text-[12px] text-[#64748B] mt-1">{b.user_name} &lt;{b.user_email}&gt; · {b.page || "no page"}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`dms-badge ${b.status === "resolved" ? "bg-[#D1FAE5] text-[#065F46]" : b.status === "in_progress" ? "bg-[#FEF3C7] text-[#92400E]" : ""}`}>{b.status || "new"}</span>
                <div className="text-xs text-[#94A3B8]">{new Date(b.created_at).toLocaleString()}</div>
              </div>
            </div>
            <div className="mt-3 text-[14px] text-[#1F2937] whitespace-pre-wrap leading-relaxed">{b.description}</div>
            {expanded === b.id && (
              <div className="mt-3 space-y-2 text-[13px] text-[#475569]">
                {b.user_action && <div><span className="dms-label">Trying to do:</span> <span className="whitespace-pre-wrap">{b.user_action}</span></div>}
                {b.browser_info && <div><span className="dms-label">Browser:</span> <span className="break-all">{b.browser_info}</span></div>}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => setExpanded(expanded === b.id ? null : b.id)} className="dms-btn-ghost text-[12.5px] py-1.5 px-3">{expanded === b.id ? "Hide details" : "Show details"}</button>
              <a href={`mailto:${b.user_email}?subject=Re: ${encodeURIComponent(b.title)}`} className="dms-btn-ghost text-[12.5px] py-1.5 px-3"><Mail className="w-3.5 h-3.5" /> Reply</a>
              {!b.is_read && <button onClick={() => update(b.id, { is_read: true })} className="dms-btn-ghost text-[12.5px] py-1.5 px-3">Mark read</button>}
              {b.status !== "in_progress" && <button onClick={() => update(b.id, { status: "in_progress" })} className="dms-btn-ghost text-[12.5px] py-1.5 px-3">In progress</button>}
              {b.status !== "resolved" && <button onClick={() => update(b.id, { status: "resolved" })} className="dms-btn-primary text-[12.5px] py-1.5 px-3">Resolved</button>}
            </div>
          </div>
         ))}
        {!loading && items.length === 0 && <div className="text-[#94A3B8] text-sm">No bug reports yet.</div>}
      </div>
    </div>
  );
}

export function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/messages"); setItems(data.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.put(`/admin/messages/${id}/read`); load(); };

  return (
    <div data-testid="admin-messages">
      <div className="dms-label mb-3">Inbox</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Contact messages</h1>
      <div className="mt-6 space-y-3">
        {loading ? <div className="text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div> :
         items.map((m) => (
          <div key={m.id} className={`dms-card p-4 sm:p-5 ${m.is_read ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold text-[#0A2342]">{m.name} <span className="font-normal text-[#64748B] text-sm">&lt;{m.email}&gt;</span></div>
                <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#64748B] mt-1">{m.topic}</div>
              </div>
              <div className="text-xs text-[#94A3B8]">{new Date(m.created_at).toLocaleString()}</div>
            </div>
            <div className="mt-3 text-[14px] text-[#1F2937] whitespace-pre-wrap leading-relaxed">{m.message}</div>
            <div className="mt-3 flex gap-2">
              <a href={`mailto:${m.email}`} className="dms-btn-ghost text-[12.5px] py-1.5 px-3"><Mail className="w-3.5 h-3.5" /> Reply</a>
              {!m.is_read && <button onClick={() => markRead(m.id)} className="dms-btn-ghost text-[12.5px] py-1.5 px-3">Mark read</button>}
            </div>
          </div>
         ))}
        {!loading && items.length === 0 && <div className="text-[#94A3B8] text-sm">No messages.</div>}
      </div>
    </div>
  );
}

export function AdminTransactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/admin/transactions").then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false)); }, []);

  return (
    <div data-testid="admin-transactions">
      <div className="dms-label mb-3">Stripe</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Transactions</h1>
      <div className="mt-6 dms-card overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead><tr className="text-left text-[11px] tracking-[0.2em] uppercase font-bold text-[#64748B] border-b border-[#E2E8F0]"><th className="p-3">Created</th><th className="p-3">Email</th><th className="p-3">Kind</th><th className="p-3">Plan/Pack</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr> :
             items.map((t) => (
              <tr key={t.session_id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                <td className="p-3 text-[#94A3B8] text-xs">{new Date(t.created_at).toLocaleString()}</td>
                <td className="p-3">{t.email}</td>
                <td className="p-3">{t.kind}</td>
                <td className="p-3">{t.plan || t.pack_id || "-"}</td>
                <td className="p-3 font-semibold">€{t.amount}</td>
                <td className="p-3"><span className="dms-badge">{t.payment_status}</span></td>
              </tr>
             ))}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#94A3B8]">No transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
